import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import SectionHeading from "@/components/base/SectionHeading";
import VideoCard from "@/components/base/VideoCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { featuredIds } from "@/mocks/videos";
import { formatCount } from "@/utils/format";
import {
  getCategories,
  getFeaturedVideos,
  getHotVideos,
  getLatestVideos,
} from "@/utils/videoData";

const highlights = [
  {
    icon: "ri-hd-line",
    title: "高清画质",
    desc: "精选高清片源，画面清晰流畅，沉浸感十足。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "清晰分类",
    desc: "影视、娱乐、综艺、动漫、纪录片，一找就有。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选",
    desc: "按地区、类型、年份、时长精准过滤，快速定位。",
  },
  {
    icon: "ri-share-forward-line",
    title: "便捷分享",
    desc: "一键复制链接或分享到社交平台，好内容不独享。",
  },
];

export default function Home() {
  usePageMeta({
    title: "一起草视频 - 高清视频分享与观看平台",
    description:
      "一起草视频为用户提供海量精选高清视频资源，涵盖影视、娱乐和短视频内容，支持在线浏览与分享，满足多样化视频需求。",
    keywords: "一起草视频,视频分享,高清视频,影视视频,短视频,在线视频观看,视频平台",
  });

  const featured = getFeaturedVideos(featuredIds);
  const categories = getCategories();
  const latest = getLatestVideos(8);
  const hot = getHotVideos(8);

  return (
    <div className="bg-background-50">
      <HeroCarousel videos={featured} />

      <section className="px-4 md:px-6 py-10 md:py-14">
        <SectionHeading
          icon="ri-apps-line"
          title="视频分类"
          subtitle="按内容方向快速进入你感兴趣的频道"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categories.map((c, idx) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className="group flex flex-col items-center gap-3 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all animate-fade-up cursor-pointer"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <span className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center group-hover:bg-accent-500 group-hover:text-primary-950 transition-colors">
                <i className={`${c.icon} text-2xl md:text-3xl`} />
              </span>
              <span className="text-sm md:text-base font-semibold text-foreground-950">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <SectionHeading
            icon="ri-fire-line"
            title="最新视频"
            subtitle="刚刚上架的高清内容，第一时间观看"
            moreLink="/list/film"
            moreText="进入视频库"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {latest.map((video, idx) => (
              <VideoCard key={video.id} video={video} index={idx} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <SectionHeading
          icon="ri-bar-chart-2-line"
          title="热门排行榜"
          subtitle="根据播放量与收藏数综合排序，发现大家都在看的内容"
          moreLink="/search"
          moreText="搜索更多"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {hot.map((video, idx) => (
            <Link
              key={video.id}
              to={`/detail/${video.id}`}
              className="group flex items-center gap-3 md:gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 hover:bg-background-100 transition-all animate-fade-up cursor-pointer"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <span
                className={`w-7 h-7 md:w-8 md:h-8 shrink-0 rounded-md flex items-center justify-center text-sm font-bold ${
                  idx < 3 ? "bg-accent-500 text-primary-950" : "bg-background-200 text-foreground-600"
                }`}
              >
                {idx + 1}
              </span>
              <div className="relative w-24 md:w-32 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                <img
                  src={video.cover}
                  alt={video.title}
                  title={`${video.title} - 热门推荐`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm md:text-base font-semibold text-foreground-950 clamp-1 group-hover:text-primary-600 transition-colors">
                  {video.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-500 clamp-1">{video.subtitle}</p>
                <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-play-circle-line text-sm" />
                    </span>
                    {formatCount(video.plays)}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 text-xs">
                    {video.categoryName}
                  </span>
                  <span className="ml-auto whitespace-nowrap">{video.duration}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <SectionHeading
            icon="ri-star-line"
            title="为什么选择一起草视频"
            subtitle="专注高清内容体验，让浏览与观看都更简单"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {highlights.map((item, idx) => (
              <div
                key={item.title}
                className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200 animate-fade-up"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary-950">
        <div className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl">
            <h2 className="text-xl md:text-3xl font-bold text-background-50">关于一起草视频</h2>
            <p className="mt-4 text-sm md:text-base text-primary-100/85 leading-relaxed">
              一起草视频是一个专注于高清视频分享与在线观看的内容平台。我们汇聚影视剧、娱乐节目、综艺、动漫与短视频等多元内容，
              通过清晰的分类与多维筛选，帮助每一位用户快速找到想看的内容。无论是通勤路上的几分钟，还是周末夜晚的一整部电影，
              一起草视频都希望让观看变得轻松、舒服、值得回味。
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              <div>
                <p className="text-2xl md:text-3xl font-extrabold text-accent-400">20万+</p>
                <p className="mt-1 text-xs md:text-sm text-primary-200/80">精选视频内容</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-extrabold text-accent-400">6 大类</p>
                <p className="mt-1 text-xs md:text-sm text-primary-200/80">内容分类频道</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-extrabold text-accent-400">1080P</p>
                <p className="mt-1 text-xs md:text-sm text-primary-200/80">高清观看体验</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-extrabold text-accent-400">全天候</p>
                <p className="mt-1 text-xs md:text-sm text-primary-200/80">随时在线观看</p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/list/film"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-bold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始浏览视频
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-700 text-background-50 text-sm font-medium hover:bg-primary-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解我们
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}